import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';

import { SelfServiceError } from '../helpers/Errors';
import { sendException, sendRetry } from '../services/Analytics';
import { SupportedErrorTypes } from './ErrorService';

let apiInternal: AxiosInstance | undefined;
let cdnInternal: AxiosInstance | undefined;

type AxiosInstanceType = 'api' | 'cdn';
type ResponseData<R> = Promise<AxiosResponse<R>['data']>;

const getBaseUrl = (type: AxiosInstanceType): string => {
  const baseUrl: string | undefined = import.meta.env.VITE_API_BASEURL;
  const cdnUrl: string | undefined = import.meta.env.VITE_CDN_BASEURL;

  switch (type) {
    case 'api':
      if (!baseUrl) {
        throw new SelfServiceError({ reason: SupportedErrorTypes.unexpectedError });
      }

      return baseUrl;
    case 'cdn':
      if (!cdnUrl) {
        throw new SelfServiceError({ reason: SupportedErrorTypes.unexpectedError });
      }

      return cdnUrl;
  }
};

const handleInstance = (instance: AxiosInstance): void => {
  initializeInterceptor(instance);
  axiosRetry(instance, {
    retries: 6,
    retryDelay: axiosRetry.exponentialDelay,
    onRetry: (retryCount: number, _, requestConfig) => {
      sendRetry(retryCount, requestConfig.url!);
    },
    retryCondition: (error: AxiosError) => {
      // both GET and get may be provided by expert app
      return error.config?.method?.toLowerCase() === 'get' && error.response?.status === 0;
    },
  });
};

const initializeInterceptor = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.response.use(function (response) {
    return response;
  }, function (error) {
    sendException(error);
    return Promise.reject(error);
  });
};

export const getApiInstance = (): AxiosInstance => {
  if (!apiInternal) {
    apiInternal = Axios.create({
      baseURL: getBaseUrl('api'),
    });
    handleInstance(apiInternal);
  }

  return apiInternal;
};

export const getCdnInstance = () => {
  if (!cdnInternal) {
    cdnInternal = Axios.create({
      baseURL: getBaseUrl('cdn'),
    });
    handleInstance(cdnInternal);
  }

  return cdnInternal;
};

export const axiosApiInstance = getApiInstance();
export const axiosCdnInstance = getCdnInstance();

const unwrapResponse = <R>(response: AxiosResponse<R>): R => response.data;

const get = (axiosInstance: AxiosInstance) => async <R>(url: string, config?: AxiosRequestConfig): ResponseData<R> => {
  return unwrapResponse(await axiosInstance.get<R>(url, config));
};

const patch = (axiosInstance: AxiosInstance) => async <R, D>(
  url: string, data?: D, config?: AxiosRequestConfig
): ResponseData<R> => {
  return unwrapResponse(await axiosInstance.patch<R, AxiosResponse<R>, D>(url, data, config));
};

const post = (axiosInstance: AxiosInstance) => async <R, D>(
  url: string, data?: D, config?: AxiosRequestConfig
): ResponseData<R> => {
  return unwrapResponse(await axiosInstance.post<R, AxiosResponse<R>, D>(url, data, config));
};

const put = (axiosInstance: AxiosInstance) => async <R, D>(
  url: string, data?: D, config?: AxiosRequestConfig
): ResponseData<R> => {
  return unwrapResponse(await axiosInstance.put<R>(url, data, config));
};

export const middlelayer = {
  get: get(axiosApiInstance),
  patch: patch(axiosApiInstance),
  post: post(axiosApiInstance),
  put: put(axiosApiInstance),
};

export const cdn = {
  get: get(axiosCdnInstance),
};
